import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Eye, Check, X, Filter, Calendar, User, Building2 } from "lucide-react";
import ApprovalTimeline from "@/components/employee/ApprovalTimeline";
import WorkflowStatusBadge from "@/components/employee/WorkflowStatusBadge";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface RemoteWorkRequest {
  id: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  requested_points: number;
  status: string;
  comment: string | null;
  created_at: string;
  processed_at: string | null;
  processed_by: string | null;
  manager_approval_status: string;
  manager_approved_at: string | null;
  manager_approver_id: string | null;
  manager_approval_reason: string | null;
  hr_approval_status: string;
  hr_approved_at: string | null;
  hr_approver_id: string | null;
  hr_approval_reason: string | null;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    position: string | null;
    manager_profile_id: string | null;
  };
}

interface RequestsTabProps {
  company: Company | null;
}

const RequestsTab = ({ company }: RequestsTabProps) => {
  const [requests, setRequests] = useState<RemoteWorkRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RemoteWorkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState<RemoteWorkRequest | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      fetchCurrentProfile();
      fetchRequests();
    }
  }, [company]);

  useEffect(() => {
    let filtered = requests;
    
    if (statusFilter === "all") {
      // Show all requests
      filtered = requests;
    } else if (statusFilter === "pending_hr") {
      // Show only requests pending HR approval (manager already approved)
      filtered = requests.filter(r => 
        r.manager_approval_status === 'approved' && 
        (r.hr_approval_status === 'pending' || r.hr_approval_status === 'not_started')
      );
    } else if (statusFilter === "pending_manager") {
      // Show requests pending manager approval (includes not_started)
      filtered = requests.filter(r => 
        r.manager_approval_status === 'pending' || r.manager_approval_status === 'not_started'
      );
    } else if (statusFilter === "approved") {
      filtered = requests.filter(r => r.hr_approval_status === 'approved');
    } else if (statusFilter === "rejected") {
      filtered = requests.filter(r => 
        r.manager_approval_status === 'rejected' || r.hr_approval_status === 'rejected'
      );
    }
    
    setFilteredRequests(filtered);
  }, [requests, statusFilter]);

  const fetchCurrentProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (profile) {
        setCurrentProfileId(profile.id);
      }
    }
  };

  const fetchRequests = async () => {
    if (!company) return;

    try {
      // Use the secure RPC function that returns all data including workflow fields
      const { data: viewData, error } = await supabase
        .rpc('get_company_remote_work_requests');

      if (error) throw error;

      // Now fetch employee details for each request
      const requestsWithEmployees = await Promise.all(
        (viewData || []).map(async (request) => {
          const { data: empData } = await supabase
            .from('employees')
            .select('id, first_name, last_name, email, position, manager_profile_id')
            .eq('id', request.employee_id)
            .single();

          return {
            ...request,
            // Map workflow fields with defaults
            manager_approval_status: request.manager_approval_status || 'pending',
            hr_approval_status: request.hr_approval_status || 'pending',
            manager_approved_at: request.manager_approved_at || null,
            hr_approved_at: request.hr_approved_at || null,
            manager_approver_id: request.manager_approver_id || null,
            hr_approver_id: request.hr_approver_id || null,
            manager_approval_reason: request.manager_approval_reason || null,
            hr_approval_reason: request.hr_approval_reason || null,
            employee: empData || {
              id: request.employee_id,
              first_name: 'N/A',
              last_name: 'N/A',
              email: 'N/A',
              position: null,
              manager_profile_id: null
            }
          };
        })
      );

      // Sort by created_at descending
      requestsWithEmployees.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setRequests(requestsWithEmployees);
    } catch (error: any) {
      console.error('Error fetching requests:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les demandes"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHRDecision = async (requestId: string, decision: 'approved' | 'rejected') => {
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) return;

      // Update HR approval fields
      const { error } = await supabase
        .from('remote_work_requests')
        .update({
          hr_approval_status: decision,
          hr_approved_at: new Date().toISOString(),
          hr_approver_id: currentProfileId,
          hr_approval_reason: comment || null,
          status: decision, // Update legacy status field
          processed_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // If approved, debit points from employee
      if (decision === 'approved') {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();

        // Get or create employee points for current month
        const { data: pointsData, error: pointsSelectError } = await supabase
          .from('employee_points')
          .select('*')
          .eq('employee_id', request.employee.id)
          .eq('month', currentMonth)
          .eq('year', currentYear)
          .maybeSingle();

        if (pointsSelectError) throw pointsSelectError;

        if (pointsData) {
          // Update existing points
          const { error: pointsUpdateError } = await supabase
            .from('employee_points')
            .update({
              used_points: pointsData.used_points + request.requested_points
            })
            .eq('id', pointsData.id);

          if (pointsUpdateError) throw pointsUpdateError;
        } else {
          // Create new points record
          const { error: pointsInsertError } = await supabase
            .from('employee_points')
            .insert({
              employee_id: request.employee.id,
              month: currentMonth,
              year: currentYear,
              total_points: 10,
              used_points: request.requested_points
            });

          if (pointsInsertError) throw pointsInsertError;
        }

        // Notify employee via edge function (reliable - bypasses RLS)
        const { data: empProfile } = await supabase
          .from('employees')
          .select('profile_id, first_name, last_name')
          .eq('id', request.employee.id)
          .single();

        if (empProfile?.profile_id) {
          await supabase.functions.invoke('send-workflow-notification', {
            body: {
              type: 'request_approved',
              request_id: requestId,
              recipient_profile_id: empProfile.profile_id,
              employee_name: `${empProfile.first_name} ${empProfile.last_name}`,
              destination_country: request.destination_country,
              start_date: request.start_date,
              end_date: request.end_date
            }
          });
        }
      } else {
        // Notify employee of rejection via edge function
        const { data: empProfile } = await supabase
          .from('employees')
          .select('profile_id, first_name, last_name')
          .eq('id', request.employee.id)
          .single();

        if (empProfile?.profile_id) {
          await supabase.functions.invoke('send-workflow-notification', {
            body: {
              type: 'request_rejected',
              request_id: requestId,
              recipient_profile_id: empProfile.profile_id,
              employee_name: `${empProfile.first_name} ${empProfile.last_name}`,
              destination_country: request.destination_country,
              start_date: request.start_date,
              end_date: request.end_date,
              reason: comment || undefined
            }
          });
        }
      }

      toast({
        title: decision === 'approved' ? "Demande approuvée" : "Demande refusée",
        description: decision === 'approved' 
          ? "La demande a été approuvée et les points ont été débités"
          : "La demande a été refusée"
      });

      setIsDetailDialogOpen(false);
      setComment("");
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour la demande"
      });
    }
  };

  const getWorkflowStatus = (request: RemoteWorkRequest) => {
    if (request.manager_approval_status === 'rejected' || request.hr_approval_status === 'rejected') {
      return 'rejected';
    }
    if (request.hr_approval_status === 'approved') {
      return 'approved';
    }
    if (request.manager_approval_status === 'approved' && 
        (request.hr_approval_status === 'pending' || request.hr_approval_status === 'not_started')) {
      return 'pending_hr';
    }
    // Default: pending manager approval (includes not_started)
    return 'pending_manager';
  };

  const openRequestDetail = (request: RemoteWorkRequest) => {
    setSelectedRequest(request);
    setComment(request.hr_approval_reason || "");
    setIsDetailDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingHRCount = requests.filter(r => 
    r.manager_approval_status === 'approved' && r.hr_approval_status === 'pending'
  ).length;

  return (
    <>
      <Card>
        <CardHeader className="space-y-3 sm:space-y-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                Validation RH
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {pendingHRCount} demande{pendingHRCount > 1 ? 's' : ''} en attente de validation RH
              </CardDescription>
            </div>
            
            {/* Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes ({requests.length})</SelectItem>
                  <SelectItem value="pending_hr">En attente RH ({pendingHRCount})</SelectItem>
                  <SelectItem value="pending_manager">En attente Manager</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Refusées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-2 sm:p-6">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {requests.length === 0 ? "Aucune demande pour le moment" : "Aucune demande ne correspond aux filtres"}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="sm:hidden space-y-3">
                {filteredRequests.map((request) => (
                  <Card key={request.id} className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">
                          {request.employee.first_name} {request.employee.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {request.employee.position || 'Non spécifié'}
                        </p>
                      </div>
                      <WorkflowStatusBadge status={getWorkflowStatus(request)} />
                    </div>
                    
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="font-medium">{request.destination_country}</span>
                      <span className="text-muted-foreground">
                        {request.requested_points} pts
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {new Date(request.start_date).toLocaleDateString('fr-FR')} - {new Date(request.end_date).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs"
                        onClick={() => openRequestDetail(request)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Détails
                      </Button>
                      
                      {getWorkflowStatus(request) === 'pending_hr' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                            onClick={() => handleHRDecision(request.id, 'approved')}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            onClick={() => handleHRDecision(request.id, 'rejected')}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employé</TableHead>
                      <TableHead>Pays</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {request.employee.first_name} {request.employee.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {request.employee.position || 'Non spécifié'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{request.destination_country}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <div className="text-sm">
                              <div>{new Date(request.start_date).toLocaleDateString('fr-FR')}</div>
                              <div className="text-muted-foreground">au {new Date(request.end_date).toLocaleDateString('fr-FR')}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{request.requested_points}</span>
                            <span className="text-xs text-muted-foreground">points</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <WorkflowStatusBadge status={getWorkflowStatus(request)} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRequestDetail(request)}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Détails
                            </Button>
                            
                            {getWorkflowStatus(request) === 'pending_hr' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700"
                                  onClick={() => handleHRDecision(request.id, 'approved')}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleHRDecision(request.id, 'rejected')}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Request Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <User className="h-4 w-4 sm:h-5 sm:w-5" />
              Détails de la demande
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Demande de {selectedRequest?.employee.first_name} {selectedRequest?.employee.last_name}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm font-medium">Employé</Label>
                  <p className="text-sm">{selectedRequest.employee.first_name} {selectedRequest.employee.last_name}</p>
                  <p className="text-xs text-muted-foreground break-all">{selectedRequest.employee.email}</p>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-medium">Poste</Label>
                  <p className="text-sm">{selectedRequest.employee.position || 'Non spécifié'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm font-medium">Pays de destination</Label>
                  <p className="text-sm">{selectedRequest.destination_country}</p>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-medium">Points demandés</Label>
                  <p className="text-sm">{selectedRequest.requested_points} points</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-sm font-medium">Date de début</Label>
                  <p className="text-sm">{new Date(selectedRequest.start_date).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Date de fin</Label>
                  <p className="text-sm">{new Date(selectedRequest.end_date).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              {/* Approval Timeline */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <ApprovalTimeline
                  managerApprovalStatus={selectedRequest.manager_approval_status || 'pending'}
                  managerApprovedAt={selectedRequest.manager_approved_at}
                  managerApprovalReason={selectedRequest.manager_approval_reason}
                  hrApprovalStatus={selectedRequest.hr_approval_status || 'not_started'}
                  hrApprovedAt={selectedRequest.hr_approved_at}
                  hrApprovalReason={selectedRequest.hr_approval_reason}
                />
              </div>

              {getWorkflowStatus(selectedRequest) === 'pending_hr' && (
                <div>
                  <Label htmlFor="comment">Commentaire RH (optionnel)</Label>
                  <Textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Ajouter un commentaire pour cette décision..."
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedRequest && getWorkflowStatus(selectedRequest) === 'pending_hr' && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleHRDecision(selectedRequest.id, 'rejected')}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="h-4 w-4 mr-2" />
                  Refuser
                </Button>
                <Button
                  onClick={() => handleHRDecision(selectedRequest.id, 'approved')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approuver
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestsTab;
